import React, { useEffect, useState, useMemo } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { createPublisher, updatePublisher } from '../../../../api/publisherApi';
import { cnpjMask, validaCNPJ } from '../../../../utils';
import { toast } from 'react-toastify';
import { Publisher } from '../../../../types/publisher';
import Select, { ActionMeta, SingleValue } from 'react-select';
import { getAdNetworks } from '../../../../api/adNetworkApi';
import { useScopes } from '../../../../hooks/useScopes';
import { ApiScope } from '../../../../types/api-scopes';
import { Option } from '../../../../utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Trash2, Plus } from 'lucide-react';

interface AddModalProps {
    publisher: Publisher | null;
    isOpen: boolean;
    toggle: () => void;
    onAddData: (newData: Publisher) => void;
    onUpdateData: (newData: Publisher) => void;
}

const AddEditPublisherModal = ({ publisher, isOpen, toggle, onAddData, onUpdateData }: AddModalProps) => {
    const { hasScope } = useScopes();
    const [loading, setLoading] = useState<boolean>(false);
    const [adNetworks, setAdNetworks] = useState<any>([]);
    const [selectAdNetworkValue, setSelectAdNetworkValue] = useState<SingleValue<Option>>();
    const [selectManagerTypeValue, setSelectManagerTypeValue] = useState<SingleValue<Option>>();

    // Manager type options
    const managerTypeOptions = useMemo(() => [
        { value: 'account', label: 'Account' },
        { value: 'inventory', label: 'Inventory' }
    ], []);

    // Check if user can edit restricted fields
    const canEditRestrictedFields = hasScope(ApiScope.PUBLISHER_WRITE) || hasScope(ApiScope.ADNETWORK_WRITE);

    const toggleAddModal = () => {
        toggle();
        handleForm.resetForm();
    };

    useEffect(() => {
        if (hasScope(ApiScope.ADNETWORK_READ)) {
            getAdNetworks().then(setAdNetworks);
        }
    }, []);

    // Initialize manager type value when publisher changes
    useEffect(() => {
        if (publisher?.managerType) {
            const managerTypeOption = managerTypeOptions.find(option => option.value === publisher.managerType);
            setSelectManagerTypeValue(managerTypeOption);
        } else {
            setSelectManagerTypeValue(undefined);
        }
    }, [publisher?.managerType, managerTypeOptions]);

    const selectAdNetworkOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectAdNetworkValue(newValue);
        handleForm.setFieldValue('adNetworkId', newValue ? Number(newValue.value) : '');
    };

    const selectManagerTypeOnChange = (newValue: SingleValue<Option>, actionMeta?: ActionMeta<Option>) => {
        setSelectManagerTypeValue(newValue);
        handleForm.setFieldValue('managerType', newValue ? newValue.value : '');
    };

    const handleAddSite = () => {
        const newSite = {
            mediaCad: '',
            cnpj: '',
            site: '',
            app: '',
            mediaKitUrl: '',
            mediaKitFile: '',
            accessibilityResources: false,
        };
        handleForm.setFieldValue('sites', [...handleForm.values.sites, newSite]);
    };

    const handleRemoveSite = (index: number) => {
        const sites = [...handleForm.values.sites];
        sites.splice(index, 1);
        handleForm.setFieldValue('sites', sites);
    };

    const handleAddUser = () => {
        const newUser = {
            name: '',
            email: '',
            birthday: '',
            jobTitle: '',
        };
        handleForm.setFieldValue('users', [...handleForm.values.users, newUser]);
    };

    const handleRemoveUser = (index: number) => {
        const users = [...handleForm.values.users];
        users.splice(index, 1);
        handleForm.setFieldValue('users', users);
    };

    const handleForm: any = useFormik({
        enableReinitialize: true,

        initialValues: {
            adNetworkId: hasScope(ApiScope.ADNETWORK_READ) ? publisher?.adNetworkId || '' : undefined,
            id: publisher?.id || undefined,
            name: publisher?.name || '',
            managerType: publisher?.managerType || '',
            networkId: publisher?.networkId || '',
            adServerCurrency: publisher?.adServerCurrency || '',
            adNetworkPublisherId: publisher?.adNetworkPublisherId || '',
            sites: publisher?.sites?.length ? publisher.sites.map(site => ({
                id: site.id,
                mediaCad: site.mediaCad || '',
                document: site.document || '',
                site: site.site || '',
                app: site.app || '',
                mediaKitUrl: site.mediaKitUrl || '',
                accessibilityResources: site.accessibilityResources || false,
            })) : [{
                mediaCad: '',
                document: '',
                site: '',
                app: '',
                mediaKitUrl: '',
                accessibilityResources: false,
            }],
            users: publisher?.users?.length ? publisher.users.map(user => ({
                id: user.id,
                name: user.name || '',
                email: user.email || '',
                birthday: user.birthday || '',
                jobTitle: user.jobTitle || '',
            })) : [],
        },

        validationSchema: Yup.object({
            name: Yup.string().trim().required('Nome é obrigatório'),
            managerType: Yup.string().optional().nullable(),
            networkId: Yup.string().optional().nullable(),
            adServerCurrency: Yup.string().optional().nullable(),
            adNetworkPublisherId: Yup.string().optional().nullable(),
            sites: Yup.array()
                .of(
                    Yup.object({
                        mediaCad: Yup.number().integer().optional().nullable(),
                        document: Yup.string()
                            .optional()
                            .nullable()
                            .test('is-valid-cnpj', 'CNPJ inválido', value => value ? validaCNPJ(value) : true),
                        site: Yup.string()
                            .test(
                                'site-or-app',
                                'O campo site ou o campo app deve ser preenchido',
                                function (value) {
                                    const { app } = this.parent;
                                    return (!value && app) || (value && !app);
                                },
                            )
                            .test(
                                'site',
                                'Site inválido. Ex.: example.com',
                                function (value: any) {
                                    if (!value) {
                                        return true;
                                    }
                                    return value.match(/^(?=.{1,255}$)([a-z0-9-.+]{2,63}\.[a-z-]{2,10}\b)$/gi);
                                },
                            )
                            .nullable(),
                        app: Yup.string()
                            .test(
                                'app-or-site',
                                'O campo site ou o campo app deve ser preenchido',
                                function (value) {
                                    const { site } = this.parent;
                                    return (!value && site) || (value && !site);
                                },
                            )
                            .test(
                                'app',
                                'O ID do aplicativo deve ser um ID válido da Apple App Store (por exemplo, id1234567890) ou um ID da Google Play Store (por exemplo, com.example.app)',
                                function (value: any) {
                                    if (!value) {
                                        return true;
                                    }
                                    const appleAppStoreRegex = /^(id)?\d+$/;
                                    const googlePlayStoreRegex = /^[a-zA-Z0-9._-]+$/;
                                    return appleAppStoreRegex.test(value) || googlePlayStoreRegex.test(value);
                                },
                            )
                            .nullable(),
                        mediaKitUrl: Yup.string().nullable().optional().url().nullable(),
                        accessibilityResources: Yup.boolean().required('Recursos de acessibilidade é obrigatório'),
                    })
                )
                .min(1, 'Adicione pelo menos um site')
                .required('Sites é obrigatório'),
            users: Yup.array()
                .of(
                    Yup.object({
                        name: Yup.string().trim().required('Nome do usuário é obrigatório'),
                        email: Yup.string().trim().email('Email inválido').required('Email é obrigatório'),
                        birthday: Yup.string().optional().nullable(),
                        jobTitle: Yup.string().trim().optional().nullable(),
                    })
                )
                .optional()
        }),
        onSubmit: (values: any) => {
            setLoading(true);

            // Prepare JSON data
            const publisherData = {
                adNetworkId: values.adNetworkId || undefined,
                id: values.id || undefined,
                name: values.name,
                managerType: values.managerType || undefined,
                networkId: values.networkId || undefined,
                adServerCurrency: values.adServerCurrency || undefined,
                adNetworkPublisherId: values.adNetworkPublisherId || undefined,
                sites: values.sites && Array.isArray(values.sites) ? values.sites.map((site: any) => ({
                    ...site,
                    id: site.id || undefined,
                    mediaCad: site.mediaCad || undefined,
                    document: site.document || undefined,
                    site: site.site || undefined,
                    app: site.app || undefined,
                    mediaKitUrl: site.mediaKitUrl || undefined,
                    accessibilityResources: site.accessibilityResources || false,
                })) : [],
                users: values.users && Array.isArray(values.users) && values.users.length > 0 ? values.users.map((user: any) => ({
                    ...user,
                    id: user.id || undefined,
                    name: user.name || undefined,
                    email: user.email || undefined,
                    birthday: user.birthday || undefined,
                    jobTitle: user.jobTitle || undefined,
                })) : []
            };
            if (publisher) {
                return updatePublisher(publisher.id, publisherData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Publisher atualizado com sucesso!');
                        onUpdateData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                createPublisher(publisherData)
                    .then((res: any) => {
                        toggleAddModal();
                        toast.success('Publisher cadastrado com sucesso!');
                        onAddData(res);
                    })
                    .catch(() => { })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggleAddModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{publisher ? 'Editar' : 'Adicionar'} publisher</DialogTitle>
                </DialogHeader>

                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }} className="space-y-4">
                    {hasScope(ApiScope.ADNETWORK_READ) && (
                        <div className="space-y-2">
                            <Label htmlFor="adNetworkId">
                                Ad Network
                            </Label>
                            <Select
                                className="basic-single"
                                classNamePrefix="select"
                                isClearable={true}
                                isSearchable={true}
                                name="adNetworkId"
                                options={adNetworks && adNetworks.map((adNetwork: any) => {
                                    return { value: adNetwork.id, label: adNetwork.name };
                                })}
                                styles={{
                                    control: (baseStyles, state) => ({
                                        ...baseStyles,
                                        borderColor: !!(handleForm.touched.adNetworkId && handleForm.errors.adNetworkId) ? 'red' : '#ced4da',
                                    }),
                                }}
                                onChange={selectAdNetworkOnChange}
                                value={selectAdNetworkValue}
                            />
                            {handleForm.touched.adNetworkId && handleForm.errors.adNetworkId && (
                                <p className="text-sm text-red-500">{handleForm.errors.adNetworkId}</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="name">
                            Nome do Publisher
                        </Label>
                        <Input
                            type="text"
                            id="name"
                            name="name"
                            onChange={handleForm.handleChange}
                            onBlur={handleForm.handleBlur}
                            value={handleForm.values.name}
                        />
                        {handleForm.touched.name && handleForm.errors.name && (
                            <p className="text-sm text-red-500">{handleForm.errors.name}</p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="managerType">
                                Manager Type
                            </Label>
                            <Select
                                options={managerTypeOptions}
                                value={selectManagerTypeValue}
                                onChange={selectManagerTypeOnChange}
                                onBlur={() => handleForm.setFieldTouched('managerType', true)}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                placeholder="Selecione o tipo de manager"
                                isClearable
                                isDisabled={!canEditRestrictedFields}
                            />
                            {handleForm.touched.managerType && handleForm.errors.managerType && (
                                <p className="text-sm text-red-500">{handleForm.errors.managerType}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="networkId">
                                Network ID
                            </Label>
                            <Input
                                type="text"
                                id="networkId"
                                name="networkId"
                                value={handleForm.values.networkId}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="Digite o Network ID"
                                disabled={!canEditRestrictedFields}
                            />
                            {handleForm.touched.networkId && handleForm.errors.networkId && (
                                <p className="text-sm text-red-500">{handleForm.errors.networkId}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="adServerCurrency">
                                Ad Server Currency
                            </Label>
                            <Input
                                type="text"
                                id="adServerCurrency"
                                name="adServerCurrency"
                                value={handleForm.values.adServerCurrency}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="Ex: USD, EUR, BRL"
                                disabled={!canEditRestrictedFields}
                            />
                            {handleForm.touched.adServerCurrency && handleForm.errors.adServerCurrency && (
                                <p className="text-sm text-red-500">{handleForm.errors.adServerCurrency}</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="adNetworkPublisherId">
                                Ad Network Publisher ID
                            </Label>
                            <Input
                                type="text"
                                id="adNetworkPublisherId"
                                name="adNetworkPublisherId"
                                value={handleForm.values.adNetworkPublisherId}
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                placeholder="ID do Publisher na Ad Network"
                                readOnly
                            />
                            {handleForm.touched.adNetworkPublisherId && handleForm.errors.adNetworkPublisherId && (
                                <p className="text-sm text-red-500">{handleForm.errors.adNetworkPublisherId}</p>
                            )}
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold mb-4">Sites</h3>

                        {handleForm.values.sites?.map((site: any, index: number) => (
                            <div key={index} className="border rounded-lg p-4 mb-4 relative space-y-4">
                                {handleForm.values.sites.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        className="absolute top-2 right-2"
                                        onClick={() => handleRemoveSite(index)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                                <h4 className="font-medium">Site #{index + 1}</h4>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor={`sites.${index}.mediaCad`}>
                                            Mídia Cad
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`sites.${index}.mediaCad`}
                                            name={`sites.${index}.mediaCad`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.mediaCad}
                                        />
                                        {handleForm.touched.sites?.[index]?.mediaCad && handleForm.errors.sites?.[index]?.mediaCad && (
                                            <p className="text-sm text-red-500">{handleForm.errors.sites[index].mediaCad}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`sites.${index}.cnpj`}>
                                            CNPJ
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`sites.${index}.cnpj`}
                                            name={`sites.${index}.cnpj`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={cnpjMask(site.cnpj)}
                                        />
                                        {handleForm.touched.sites?.[index]?.cnpj && handleForm.errors.sites?.[index]?.cnpj && (
                                            <p className="text-sm text-red-500">{handleForm.errors.sites[index].cnpj}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor={`sites.${index}.site`}>
                                            Site
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`sites.${index}.site`}
                                            name={`sites.${index}.site`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.site}
                                        />
                                        {handleForm.touched.sites?.[index]?.site && handleForm.errors.sites?.[index]?.site && (
                                            <p className="text-sm text-red-500">{handleForm.errors.sites[index].site}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`sites.${index}.app`}>
                                            App
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`sites.${index}.app`}
                                            name={`sites.${index}.app`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={site.app}
                                        />
                                        {handleForm.touched.sites?.[index]?.app && handleForm.errors.sites?.[index]?.app && (
                                            <p className="text-sm text-red-500">{handleForm.errors.sites[index].app}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor={`sites.${index}.mediaKitUrl`}>
                                        Mídia Kit - URL
                                    </Label>
                                    {site.mediaKitUrl && (
                                        <a href={site.mediaKitUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600">
                                            <i className={'ri-external-link-line'}></i>
                                        </a>
                                    )}
                                    <Input
                                        type="text"
                                        id={`sites.${index}.mediaKitUrl`}
                                        name={`sites.${index}.mediaKitUrl`}
                                        onChange={handleForm.handleChange}
                                        onBlur={handleForm.handleBlur}
                                        value={site.mediaKitUrl}
                                    />
                                    {handleForm.touched.sites?.[index]?.mediaKitUrl && handleForm.errors.sites?.[index]?.mediaKitUrl && (
                                        <p className="text-sm text-red-500">{handleForm.errors.sites[index].mediaKitUrl}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor={`sites.${index}.accessibilityResources`}>
                                        Recursos de acessibilidade
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id={`sites.${index}.accessibilityResources`}
                                            name={`sites.${index}.accessibilityResources`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            checked={site.accessibilityResources}
                                            className="w-4 h-4"
                                        />
                                        <Label htmlFor={`sites.${index}.accessibilityResources`} className="cursor-pointer">
                                            {site.accessibilityResources ? 'Ativado' : 'Desativado'}
                                        </Label>
                                    </div>
                                    {handleForm.touched.sites?.[index]?.accessibilityResources && handleForm.errors.sites?.[index]?.accessibilityResources && (
                                        <p className="text-sm text-red-500">{handleForm.errors.sites[index].accessibilityResources}</p>
                                    )}
                                </div>
                            </div>
                        ))}

                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleAddSite}
                            className="flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Adicionar Site
                        </Button>
                    </div>

                    <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold mb-4">Usuários <span className="text-muted-foreground font-normal">(Opcional)</span></h3>

                        {handleForm.values.users && handleForm.values.users.length > 0 ? handleForm.values.users.map((user: any, index: number) => (
                            <div key={index} className="border rounded-lg p-4 mb-4 relative space-y-4">
                                {handleForm.values.users.length > 1 && (
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        className="absolute top-2 right-2"
                                        onClick={() => handleRemoveUser(index)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                                <h4 className="font-medium">Usuário #{index + 1}</h4>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor={`users.${index}.name`}>
                                            Nome <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`users.${index}.name`}
                                            name={`users.${index}.name`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.name}
                                        />
                                        {handleForm.touched.users?.[index]?.name && handleForm.errors.users?.[index]?.name && (
                                            <p className="text-sm text-red-500">{handleForm.errors.users[index].name}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`users.${index}.email`}>
                                            Email <span className="text-red-500">*</span>
                                        </Label>
                                        <Input
                                            type="email"
                                            id={`users.${index}.email`}
                                            name={`users.${index}.email`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.email}
                                        />
                                        {handleForm.touched.users?.[index]?.email && handleForm.errors.users?.[index]?.email && (
                                            <p className="text-sm text-red-500">{handleForm.errors.users[index].email}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor={`users.${index}.birthday`}>
                                            Data de Nascimento
                                        </Label>
                                        <Input
                                            type="date"
                                            id={`users.${index}.birthday`}
                                            name={`users.${index}.birthday`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.birthday}
                                        />
                                        {handleForm.touched.users?.[index]?.birthday && handleForm.errors.users?.[index]?.birthday && (
                                            <p className="text-sm text-red-500">{handleForm.errors.users[index].birthday}</p>
                                        )}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor={`users.${index}.jobTitle`}>
                                            Cargo
                                        </Label>
                                        <Input
                                            type="text"
                                            id={`users.${index}.jobTitle`}
                                            name={`users.${index}.jobTitle`}
                                            onChange={handleForm.handleChange}
                                            onBlur={handleForm.handleBlur}
                                            value={user.jobTitle}
                                        />
                                        {handleForm.touched.users?.[index]?.jobTitle && handleForm.errors.users?.[index]?.jobTitle && (
                                            <p className="text-sm text-red-500">{handleForm.errors.users[index].jobTitle}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="text-center text-muted-foreground py-8">
                                <p>Nenhum usuário adicionado. Clique em "Adicionar Usuário" para adicionar usuários ao publisher.</p>
                            </div>
                        )}

                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleAddUser}
                            className="flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Adicionar Usuário
                        </Button>
                    </div>

                    <DialogFooter>
                        <Button type="submit" disabled={loading} className="flex items-center gap-2">
                            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                            {publisher ? 'Atualizar' : 'Cadastrar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddEditPublisherModal;
